import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Row, Alert, Button, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import axiosInstance from '../../../api/axios';
import { useAuth } from '../../../_helper/Auth/AuthProvider';

const ReportingSettings = () => {
  const { userData } = useAuth();
  const role = userData?.role;
  const isAdmin = role === 'admin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState('statement_period');

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await axiosInstance.get('/settings/reporting');
      setModes(Array.isArray(data?.modes) ? data.modes : []);
      if (data?.reporting_mode) {
        setSelectedMode(data.reporting_mode);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not load reporting settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!isAdmin) return;
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const { data } = await axiosInstance.patch('/settings/reporting', {
        reporting_mode: selectedMode,
      });
      if (data?.reporting_mode) {
        setSelectedMode(data.reporting_mode);
      }
      setMessage(data?.message || 'Reporting mode updated.');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedDefinition = modes.find((mode) => mode.value === selectedMode);

  return (
    <>
      <Breadcrumbs parent="Settings" title="Reporting mode" mainTitle="Reporting mode" />
      <Container fluid>
        <Row>
          <Col lg="8" xl="6">
            <Card>
              <CardHeader>
                <h5 className="mb-0">Royalties reporting mode</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Spinner size="sm" /> Loading…
                  </div>
                ) : (
                  <>
                    {error && <Alert color="danger">{error}</Alert>}
                    {message && <Alert color="success">{message}</Alert>}

                    <p className="text-muted">
                      Choose which date fields drive quarter and month filters for streaming royalties.
                      This setting applies to the CMS royalties UI and all Accounting royalties APIs.
                    </p>

                    <FormGroup>
                      <Label for="accounting-reporting-mode">Mode</Label>
                      <Input
                        id="accounting-reporting-mode"
                        type="select"
                        value={selectedMode}
                        disabled={!isAdmin || saving}
                        onChange={(e) => setSelectedMode(e.target.value)}
                      >
                        {modes.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    {selectedDefinition?.description && (
                      <p className="small text-muted">{selectedDefinition.description}</p>
                    )}

                    {isAdmin ? (
                      <Button color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    ) : (
                      <Alert color="light" className="mb-0">
                        Only administrators can change this setting. Current mode:{' '}
                        <strong>{selectedDefinition?.label || selectedMode}</strong>.
                      </Alert>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ReportingSettings;
